/**
 * Leaderboard Modal Component
 * Shows top players by VibeCoins, Quests, and Gifts
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Trophy, Coins, Award, Gift, X } from 'lucide-react';

const TABS = [
  { id: 'vibecoins', label: 'VibeCoins', icon: Coins, color: 'text-yellow-400' },
  { id: 'quests', label: 'Quests', icon: Award, color: 'text-green-400' },
  { id: 'gifts', label: 'Gifts', icon: Gift, color: 'text-pink-400' },
];

const LeaderboardModal = ({
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('vibecoins');
  const [leaderboards, setLeaderboards] = useState({ vibecoins: [], quests: [], gifts: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchLeaderboards();
    }
  }, [isOpen]);

  const fetchLeaderboards = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/nightclub/leaderboards`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboards(data);
      }
    } catch (e) {
      console.error('Failed to fetch leaderboards:', e);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const currentData = leaderboards[activeTab] || [];

  const getRankBadge = (index) => {
    switch (index) {
      case 0: return { emoji: '🥇', bg: 'bg-yellow-500/20', border: 'border-yellow-500' };
      case 1: return { emoji: '🥈', bg: 'bg-gray-400/20', border: 'border-gray-400' };
      case 2: return { emoji: '🥉', bg: 'bg-amber-700/20', border: 'border-amber-700' };
      default: return { emoji: `#${index + 1}`, bg: 'bg-white/5', border: 'border-white/10' };
    }
  };

  const formatValue = (item) => {
    if (activeTab === 'vibecoins') {
      return `💰 ${item.vibecoins?.toLocaleString() || 0}`;
    } else if (activeTab === 'quests') {
      return `✅ ${item.completed_count || 0} completed`;
    } else {
      return `🎁 ${item.gifts_sent || 0} gifts (${item.total_value?.toLocaleString() || 0} value)`;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="leaderboard-overlay"
    >
      <Card 
        className="w-full max-w-md bg-gray-900 border-yellow-500/30 max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="leaderboard-modal"
      >
        <CardHeader className="pb-2 shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Leaderboards
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-leaderboard">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex gap-1 mt-3">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-all text-sm ${
                    activeTab === tab.id 
                      ? 'bg-yellow-500/20 border border-yellow-500/50 text-yellow-400' 
                      : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className={`w-4 h-4 ${activeTab === tab.id ? tab.color : ''}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto space-y-2 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
            </div>
          ) : currentData.length === 0 ? (
            <div className="text-center py-8 text-white/40">
              No data yet. Be the first on the leaderboard!
            </div>
          ) : (
            currentData.map((item, index) => {
              const rank = getRankBadge(index);
              return (
                <div 
                  key={item.user_id || index}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${rank.bg} ${rank.border}`}
                  data-testid={`leaderboard-entry-${index}`}
                >
                  {/* Rank */}
                  <div className="w-8 h-8 flex items-center justify-center text-lg font-bold">
                    {rank.emoji}
                  </div>
                  
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden">
                    {item.picture ? (
                      <img src={item.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-lg">👤</span>
                    )}
                  </div>
                  
                  {/* Name & Value */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{item.name}</p>
                    <p className="text-white/60 text-sm">{formatValue(item)}</p>
                  </div>
                  
                  {/* Role badge */}
                  {item.role && item.role !== 'user' && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      item.role === 'owner' ? 'bg-red-500/20 text-red-400' :
                      item.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                      item.role === 'overseer' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {item.role}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaderboardModal;
